import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import BreadcrumbCompany from "../BreadcrumbCompany";
import { useEffect, useMemo, useState } from "react";
import { UrlPath } from "utility/routes";
import ProductList from "components/ProductList/ProductList";
import { webShopService } from "services/Services";
import { BulletPointsConfigModel, ExtraLinksConfigModel, ExtraLinksConfigWrapper, FlattenedProductConfigurationsResponse, InfoTextConfigModel, LogoConfigModel, ProductConfigModel, SearchWordsConfigModel, UpdateConfigurationRequest } from "models/WebShop";
import WebShopService from "services/WebShopService";
import LoadingScreen from "components/LoadingScreen";
import LogoConfig from "./Configurations/LogoConfig";
import InfoTextConfig from "./Configurations/InfoTextConfig";
import ExtraLinksConfig from "./Configurations/ExtraLinksConfig";
import BulletPointsConfig from "./Configurations/BulletPointsConfig";
import { useProductConfiguration } from "context/useProductConfiguration";
import SearchWordsConfig from "./Configurations/SearchWordsConfig";
import { useConfigPermission } from "context/useConfigPermission";
import { Seo } from "components/Seo";


const ProductConfig: React.FC = () => {

    const params = useParams();
    const navigate = useNavigate();
    const { productConfigurationsByCompany, fetchProductConfigurations } = useProductConfiguration();

    const [configurations, setConfigurations] = useState<FlattenedProductConfigurationsResponse | null>(null);
    const [currentConfig, setCurrentConfig] = useState<ProductConfigModel[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [formValid, setFormValid] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const { canConfig } = useConfigPermission();


    const memoizedLogoConfig = useMemo(() => [configurations?.logo as LogoConfigModel], [configurations?.logo]);
    const memoizedInfoTextConfig = useMemo(() => [configurations?.infoText as InfoTextConfigModel], [configurations?.infoText]);
    const memoizedExtraLinksConfig = useMemo(() => configurations?.extraLinks ?? [], [configurations?.extraLinks]);
    const memoizedBulletPointsConfig = useMemo(() => [configurations?.bulletPoints as BulletPointsConfigModel], [configurations?.bulletPoints]);
    const memoizedSearchWordsConfig = useMemo(() => [configurations?.searchWords as SearchWordsConfigModel], [configurations?.searchWords]);


    let editElement = <LoadingScreen />;
    let title: string | undefined;

    useEffect(() => {
        const fetchConfig = async () => {
            const response = await productConfigurationsByCompany[params.id as string]
            if (response !== undefined) {
                const flattenedResponse: FlattenedProductConfigurationsResponse = {
                    ...response,
                    extraLinks: response.extraLinks?.extraLinks,
                };
                setConfigurations(flattenedResponse);
            }
            setLoading(false);
        };
        fetchConfig();
    }, []);


    const handleFormValidation = (valid: boolean) => {
        setFormValid(valid);

    }

    const handleCurrentProduct = (config: ProductConfigModel[]) => {
        setCurrentConfig(config);
    }

    function isLogoProduct(configs: ProductConfigModel[]): configs is LogoConfigModel[] {
        return configs.every(
            (config): config is LogoConfigModel =>
                (config as LogoConfigModel).publicUrlTransform !== undefined
        );
    }

    function isExtraLinksProduct(configs: ProductConfigModel[]): configs is ExtraLinksConfigModel[] {
        return configs.every(
            (config): config is ExtraLinksConfigModel =>
                typeof (config as ExtraLinksConfigModel).url === "string" &&
                typeof (config as ExtraLinksConfigModel).label === "string"
        );
    }

    function isInfoTextProduct(configs: ProductConfigModel[]): configs is InfoTextConfigModel[] {
        return configs.every(
            (config): config is InfoTextConfigModel =>
                typeof (config as InfoTextConfigModel).text === "string"
        );
    }

    function isBulletPointsProduct(configs: ProductConfigModel[]): configs is BulletPointsConfigModel[] {
        return configs.every(
            (config): config is BulletPointsConfigModel =>
                (config as BulletPointsConfigModel).bulletPoints !== undefined
        )
    }

    function isSearchWordsProduct(configs: ProductConfigModel[]): configs is SearchWordsConfigModel[] {
        return configs.every(
            (config): config is SearchWordsConfigModel =>
                typeof (config as SearchWordsConfigModel).searchWords === "string"
        );
    }

    const onSubmit = async (e: { preventDefault: () => void; }) => {
        e.preventDefault();
        setSpinner(true);

        try {
            let updateRequest: UpdateConfigurationRequest = { documentId: params.id as string };

            if (currentConfig === null)
                return;

            if (isLogoProduct(currentConfig)) {
                updateRequest.logo = currentConfig[0];
            }
            else if (isExtraLinksProduct(currentConfig)) {
                let extraLinkWrapper: ExtraLinksConfigWrapper = { extraLinks: currentConfig }
                updateRequest.extraLinks = extraLinkWrapper;
            }
            else if (isInfoTextProduct(currentConfig)) {
                updateRequest.infoText = currentConfig[0];
            }
            else if (isBulletPointsProduct(currentConfig)) {
                updateRequest.bulletPoints = currentConfig[0];
            }
            else if (isSearchWordsProduct(currentConfig)) {
                updateRequest.searchWords = currentConfig[0];
            }
            else {
                return;
            }

            const response = await webShopService.updateProductConfiguration(updateRequest);

            fetchProductConfigurations(params.id as string);

            navigate(-1);

        }
        catch (exception) {
        }
        finally {
            setSpinner(false);
        }
    }

    if (!loading) {
        if (!canConfig(params.id as string)) {
            title = "Manglende rettigheter";
            editElement = <>Brukeren din mangler rettigheter til å endre kjøpte produkter.</>
        }
        else {

            switch (params.product) {

                case UrlPath.Account.ProductConfig.Logo: {
                    title = "Vis logo"
                    editElement = <LogoConfig handleOnChange={handleCurrentProduct} config={memoizedLogoConfig} handleFormValidation={handleFormValidation} />
                    break;
                }
                case UrlPath.Account.ProductConfig.InfoText: {
                    title = "Beskrivelse av bedriften"
                    editElement = <InfoTextConfig handleOnChange={handleCurrentProduct} config={memoizedInfoTextConfig} handleFormValidation={handleFormValidation} />
                    break;
                }
                case UrlPath.Account.ProductConfig.ExtraLinks: {
                    title = "Ekstra lenker"
                    editElement = <ExtraLinksConfig handleOnChange={handleCurrentProduct} config={memoizedExtraLinksConfig} handleFormValidation={handleFormValidation} />
                    break;
                }
                case UrlPath.Account.ProductConfig.BulletPoints: {
                    title = "Punktliste: Bedriftens varer og tjenester"
                    editElement = <BulletPointsConfig handleOnChange={handleCurrentProduct} config={memoizedBulletPointsConfig} handleFormValidation={handleFormValidation} />
                    break;
                }
                case UrlPath.Account.ProductConfig.SearchWords: {
                    title = "Søkeord"
                    editElement = <SearchWordsConfig handleOnChange={handleCurrentProduct} config={memoizedSearchWordsConfig} handleFormValidation={handleFormValidation} />
                    break;
                }
                default: {
                    title = "Produktet mangler";
                    editElement = <>Må implementeres.</>
                    break;
                }
            }
        }
    }

    return <>
        <BreadcrumbCompany />
        <Seo title={"Endre - " + title} />
        <div className="card">
            <div className="card-body">
                <div className="mb-4 pb-3 d-flex flex-column flex-md-row justify-content-between align-items-baseline gap-2 cc-shop-detail-head">
                    <h1 className="h3 mb-0">{title}</h1>
                </div>
                {!loading ? editElement : null}
                <br></br>
                <p className="text-muted mb-0 lh-sm cc-fs-xs ">Endringer på kjøpte produkter vil bli behandlet fortløpende før det vises på nett.</p>
                <div className="d-md-flex justify-content-between align-items-center mb-0 mt-4">
                    <div className="order-first">
                        <button type="button" className="btn btn-primary me-1" disabled={!formValid || spinner} onClick={onSubmit}>Lagre {spinner ? (<span className="spinner-border spinner-border-sm"></span>) : null}</button>
                        <button onClick={() => navigate(-1)} disabled={spinner} className="btn btn-outline-primary">Avbryt</button>
                    </div>
                </div>

            </div>
        </div>
        <ProductList />
    </>
}

export default ProductConfig;